/**
 * Hook system for inter-module communication.
 *
 * Modules communicate through events, never by direct imports.
 * This prevents tight coupling between modules.
 */

/** Hook context — passed to every handler so hooks can access DB, etc. */
export interface HookContext {
  db: unknown // DrizzleD1Database — typed as unknown to avoid drizzle dep in shared
  [key: string]: unknown
}

/** Known module events with their payload types */
export interface ModuleEvents {
  // Core events
  'core.version.snapshot': {
    companyId: string
    module: string
    doctype: string
    documentId: string
    userId: string
    beforeSnapshot: Record<string, unknown>
    afterSnapshot: Record<string, unknown>
    action?: 'updated' | 'state_changed' | 'created'
    summary?: string
  }
  'core.user.created': { userId: string }
  'core.user.login': { userId: string }
  'core.user.updated': { userId: string }
  'core.company.created': { companyId: string; userId: string }
  'core.module.enabled': { companyId: string; module: string; userId: string }
  'core.module.disabled': { companyId: string; module: string; userId: string }

  // Accounting events (emitted by accounting module)
  'accounting.invoice.created': {
    invoiceId: string
    invoiceNo: string
    /** Upstream record this invoice was created from (e.g. rental booking id) */
    referenceId?: string
    /** Module that originated the invoice (e.g. MODULES.CAR_RENTAL, MODULES.SALES) */
    sourceModule?: string
  }
  'accounting.invoice.submitted': { invoiceId: string; companyId: string }
  'accounting.invoice.cancelled': { invoiceId: string; companyId: string }
  'accounting.invoice.paid': { invoiceId: string }
  'accounting.invoice.updated': { invoiceId: string }
  'accounting.invoice.outstanding.updated': {
    invoiceId: string
    companyId: string
    invoiceTotal: number
    outstanding: number
    totalAllocated: number
  }
  'accounting.payment.created': {
    paymentId: string
    paymentNo: string
    /** Upstream record this payment was created from */
    referenceId?: string
    /** Module that originated the payment */
    sourceModule?: string
  }
  'accounting.payment.submitted': { paymentId: string }
  'accounting.payment.cancelled': { paymentId: string }

  // Rental events (emitted by rental module)
  'rental.booking.created': { bookingId: string }
  'rental.booking.updated': { bookingId: string }
  'rental.booking.cancelled': { bookingId: string }
  'rental.booking.stateChanged': { bookingId: string; from: string; to: string }
  'rental.vehicle.picked_up': { vehicleId: string; bookingId: string }
  'rental.vehicle.returned': { vehicleId: string; bookingId: string }

  // Rental → Accounting request events (rental asks accounting to create documents)
  'rental.request.createInvoice': {
    bookingId: string
    companyId: string
    branchId?: string | null
    customerId: string
    invoiceDate: string
    bookingNo: string
    rentalSubtotal: number
    securityDeposit: number
    taxAmount: number
    invoiceTotal: number
    rentalDays: number
    dailyRate: number
    remarks?: string
    createdBy: string | null
  }
  'rental.request.createPayment': {
    bookingId: string
    companyId: string
    branchId?: string | null
    customerId: string
    invoiceId: string | null
    paymentType: 'receive' | 'pay'
    amount: number
    paymentMethod: 'cash' | 'transfer' | 'credit_card' | 'promptpay'
    paymentDate: string
    paymentPurpose: 'deposit' | 'balance' | 'refund' | 'refund_security_deposit'
    referenceNo?: string | null
    bankAccountId?: string | null
    remarks?: string
    createdBy: string | null
    bookingNo: string
  }
  'rental.request.cancelPayment': {
    paymentId: string
    userId?: string
  }
  'rental.request.cancelInvoice': {
    invoiceId: string
    userId?: string
  }
  'rental.request.refundSecurityDeposit': {
    bookingId: string
    companyId: string
    branchId?: string | null
    customerId: string
    penaltyAmount: number
    bookingNo: string
    paymentDate: string
    remarks?: string
    createdBy: string | null
  }
  'rental.request.cancelJournalEntries': {
    bookingId: string
    userId?: string
  }

  // Workflow events (emitted by workflow engine)
  'workflow.state.updated': {
    workflowId: string
    module: string
    doctype: string
    documentId: string
    fromStateKey: string
    toStateKey: string
    userId: string
    docStatus?: number | null  // 0=Draft, 1=Submitted, 2=Cancelled (from target state mapping)
  }

  // Car-Rental action hooks (emitted by workflow engine via actionHook)
  'car-rental.action.confirmBooking': {
    workflowId: string;
    documentId: string;
    fromStateKey: string;
    toStateKey: string;
    formData?: Record<string, unknown>;
    userId?: string;
    transitionId: string;
  }
  'car-rental.action.receiveDeposit': {
    workflowId: string;
    documentId: string;
    fromStateKey: string;
    toStateKey: string;
    formData?: Record<string, unknown>;
    userId?: string;
    transitionId: string;
  }
  'car-rental.action.releaseVehicle': {
    workflowId: string;
    documentId: string;
    fromStateKey: string;
    toStateKey: string;
    formData?: Record<string, unknown>;
    userId?: string;
    transitionId: string;
  }
  'car-rental.action.returnVehicle': {
    workflowId: string;
    documentId: string;
    fromStateKey: string;
    toStateKey: string;
    formData?: Record<string, unknown>;
    userId?: string;
    transitionId: string;
  }
  'car-rental.action.refundSecurity': {
    workflowId: string;
    documentId: string;
    fromStateKey: string;
    toStateKey: string;
    formData?: Record<string, unknown>;
    userId?: string;
    transitionId: string;
  }
  'car-rental.action.closeBooking': {
    workflowId: string;
    documentId: string;
    fromStateKey: string;
    toStateKey: string;
    formData?: Record<string, unknown>;
    userId?: string;
    transitionId: string;
  }
  'car-rental.action.cancelBooking': {
    workflowId: string;
    documentId: string;
    fromStateKey: string;
    toStateKey: string;
    formData?: Record<string, unknown>;
    userId?: string;
    transitionId: string;
  }

  // CRM events
  'crm.lead.updated': { leadId: string; companyId: string }
  'crm.deal.updated': { dealId: string; companyId: string }
  'crm.lead.created': { leadId: string; companyId: string }
  'crm.lead.converted': { leadId: string; dealId: string; contactId: string; organizationId: string | null }
  'crm.deal.created': { dealId: string; companyId: string }
  'crm.deal.stageChanged': { dealId: string; fromStageId: string; toStageId: string }
  'crm.deal.won': { dealId: string; companyId: string; contactId: string | null; organizationId: string | null }
  'crm.deal.lost': { dealId: string; companyId: string; lostReasonId: string | null }

  // CRM action hooks (emitted by workflow engine via actionHook)
  'crm.action.dealWon': {
    workflowId: string;
    documentId: string;
    fromStateKey: string;
    toStateKey: string;
    formData?: Record<string, unknown>;
    userId?: string;
    transitionId: string;
  }
  'crm.action.dealLost': {
    workflowId: string;
    documentId: string;
    fromStateKey: string;
    toStateKey: string;
    formData?: Record<string, unknown>;
    userId?: string;
    transitionId: string;
  }
  'crm.action.convertLead': {
    workflowId: string;
    documentId: string;
    fromStateKey: string;
    toStateKey: string;
    formData?: Record<string, unknown>;
    userId?: string;
    transitionId: string;
  }

  // Email trigger events (consumed by core:email hooks)
  'booking.confirmed': {
    companyId: string
    bookingId: string
    customerEmail?: string
    customerName?: string
    bookingNumber?: string
  }
  'payment.received': {
    companyId: string
    paymentId: string
    customerEmail?: string
    customerName?: string
    paymentNumber?: string
    amount?: number
  }
  'invoice.submitted': {
    companyId: string
    invoiceId: string
    customerEmail?: string
    customerName?: string
    invoiceNumber?: string
  }

  // CRM → Accounting REQUEST events
  'crm.request.createCustomer': {
    companyId: string
    contactId: string | null
    organizationId: string | null
    name: string
    email: string | null
    phone: string | null
    address: string | null
  }

  // Sales lifecycle events (Epic #442 — Sales→Accounting GL bridge).
  // Emitted by accounting/services/invoice.service.ts after a sales-flavoured
  // invoice flips status. The accounting module subscribes to these to run
  // the idempotent `postInvoiceGl` / `reverseInvoiceGl` helpers, so cross-
  // module callers (POS, sales-direct submit) get GL posting for free
  // without coupling to invoice.service internals.
  'sales.invoice.submitted': { invoiceId: string; companyId: string }
  'sales.invoice.cancelled': { invoiceId: string; companyId: string }
  'sales.creditNote.submitted': { invoiceId: string; companyId: string }

  // Inventory lifecycle events (Epic #443 — Sales→Inventory + COGS bridge).
  //
  // `inventory.invoice.stockDeducted` fires from inventory's
  // `sales.invoice.submitted` listener AFTER stock entries are written and
  // valuation_rate is snapshotted. The accounting COGS poster subscribes
  // to this so the COGS JE only posts when the stock data it depends on
  // is committed — solves the registration-order problem with two
  // accounting hooks reading inventory state.
  //
  // The cancelled / returned variants exist for symmetry but are currently
  // wired to listeners that do their own idempotency checks (we don't
  // strictly need them — kept for forward compatibility / observability).
  'inventory.invoice.stockDeducted': { invoiceId: string; companyId: string }
  'inventory.invoice.stockReversed': { invoiceId: string; companyId: string }
  'inventory.creditNote.stockReturned': { invoiceId: string; companyId: string }
}

/** Event name type */
export type EventName = keyof ModuleEvents

/** Hook handler function — can return a result object collected by emitWithResult */
export type HookHandler<E extends EventName = EventName> = (
  data: ModuleEvents[E],
  context?: HookContext
) => void | Record<string, unknown> | Promise<void | Record<string, unknown>>

/** Registered hook entry */
interface HookEntry {
  event: EventName
  handler: HookHandler<EventName>
  module: string
  priority: number
}

/**
 * Hook registry for event-based inter-module communication.
 *
 * @example
 * ```ts
 * const hooks = new HookRegistry()
 *
 * // Module A listens
 * hooks.on('accounting.payment.created', 'car-rental', async (data) => {
 *   await updateBookingPayment(data.paymentId, data.entityId)
 * })
 *
 * // Module B emits
 * await hooks.emit('accounting.payment.created', {
 *   paymentId: '01ARZ...',
 *   entityId: '01BRX...',
 * })
 * ```
 */
export class HookRegistry {
  private hooks: HookEntry[] = []

  /**
   * Register a handler for an event
   *
   * @param event - The event name to listen to
   * @param module - The module registering this handler
   * @param handler - The handler function
   * @param priority - Lower numbers run first (default: 10)
   */
  on<E extends EventName>(
    event: E,
    module: string,
    handler: HookHandler<E>,
    priority: number = 10
  ): void {
    this.hooks.push({
      event,
      handler: handler as HookHandler<EventName>,
      module,
      priority,
    })

    // Keep sorted by priority
    this.hooks.sort((a, b) => a.priority - b.priority)
  }

  /**
   * Remove all handlers for a specific module
   */
  offModule(module: string): void {
    this.hooks = this.hooks.filter((h) => h.module !== module)
  }

  /**
   * Remove all handlers for a specific event
   */
  offEvent(event: EventName): void {
    this.hooks = this.hooks.filter((h) => h.event !== event)
  }

  /**
   * Emit an event, calling all registered handlers in priority order
   *
   * @param event - The event to emit
   * @param data - The event payload
   * @param context - Optional context (e.g., db connection, tenant info)
   * @param onProgress - Optional callback called after each handler completes
   */
  async emit<E extends EventName>(
    event: E,
    data: ModuleEvents[E],
    context?: HookContext,
    onProgress?: (info: { module: string; index: number; total: number; error?: unknown }) => void
  ): Promise<void> {
    const handlers = this.hooks.filter((h) => h.event === event)

    for (let i = 0; i < handlers.length; i++) {
      const entry = handlers[i]
      try {
        await entry.handler(data as ModuleEvents[EventName], context)
        onProgress?.({ module: entry.module, index: i, total: handlers.length })
      } catch (error) {
        console.error(
          `[HookRegistry] Error in handler for "${event}" from module "${entry.module}":`,
          error
        )
        // When onProgress is provided, report error and continue (don't stop other modules)
        if (onProgress) {
          onProgress({ module: entry.module, index: i, total: handlers.length, error })
        } else {
          throw error
        }
      }
    }
  }

  /**
   * Emit an event and collect results from handlers.
   *
   * Used when the emitter needs data back from the handler
   * (e.g., rental emits createInvoice, accounting returns invoiceId).
   *
   * Returns merged results from all handlers.
   */
  async emitWithResult<E extends EventName>(
    event: E,
    data: ModuleEvents[E],
    context?: HookContext
  ): Promise<Record<string, unknown>> {
    const handlers = this.hooks.filter((h) => h.event === event)
    let results: Record<string, unknown> = {}

    for (const entry of handlers) {
      try {
        const result = await entry.handler(data as ModuleEvents[EventName], context)
        if (result && typeof result === 'object') {
          results = { ...results, ...result }
        }
      } catch (error) {
        console.error(
          `[HookRegistry] Error in handler for "${event}" from module "${entry.module}":`,
          error
        )
        throw error
      }
    }

    return results
  }

  /**
   * Get count of handlers for an event
   */
  listenerCount(event: EventName): number {
    return this.hooks.filter((h) => h.event === event).length
  }

  /**
   * Get all registered events
   */
  getRegisteredEvents(): EventName[] {
    return [...new Set(this.hooks.map((h) => h.event))]
  }

  /**
   * Clear all handlers (useful for testing)
   */
  clear(): void {
    this.hooks = []
  }
}

/**
 * Global hook registry singleton
 */
export const hookRegistry = new HookRegistry()
